/** Timeouts and one retry for sign-up and onboarding calls. */

export const SETUP_TIMEOUT_MS = 12000;

const REQUEST_ID_KEY = 'pik_signup_request_id';

export function isSetupTimeout(err) {
  const code = err?.code || '';
  const name = err?.name || '';
  const message = String(err?.message || '');
  return code === 'ECONNABORTED'
    || name === 'AbortError'
    || name === 'TimeoutError'
    || /timeout/i.test(message);
}

export function isRetryableSetupError(err) {
  if (!err) return false;
  if (isSetupTimeout(err)) return true;
  const status = err?.response?.status || err?.status;
  if (!status) return true;
  return status === 408 || status === 429 || status >= 500;
}

export async function withSetupRetry(fn, attempts = 2) {
  let last;
  for (let i = 0; i < attempts; i += 1) {
    try {
      return await fn();
    } catch (err) {
      last = err;
      if (!isRetryableSetupError(err) || i === attempts - 1) throw err;
    }
  }
  throw last;
}

export function setupFailureMessage(err, fallback) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (isSetupTimeout(err) || !err?.response) {
    return fallback || 'The connection timed out. What you typed is still here. Try again.';
  }
  return fallback || 'Could not reach PeopleiKnow. What you typed is still here. Try again.';
}

export function cleanRequestId(value) {
  const text = String(value || '').trim();
  if (text.length < 8 || text.length > 80) return '';
  if (/\s/.test(text)) return '';
  return text;
}

export function mintRequestId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `req-${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;
}

export function signupRequestId() {
  try {
    const saved = cleanRequestId(sessionStorage.getItem(REQUEST_ID_KEY));
    if (saved) return saved;
    const next = cleanRequestId(mintRequestId()) || mintRequestId();
    sessionStorage.setItem(REQUEST_ID_KEY, next);
    return next;
  } catch {
    return mintRequestId();
  }
}

export function clearSignupRequestId() {
  try { sessionStorage.removeItem(REQUEST_ID_KEY); } catch { /* private mode */ }
}
